// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table row component for displaying detailed asset information.
 * This module provides a configurable row view that adapts its display
 * based on the asset's status and the selected columns. The row handles
 * various asset states and provides visual feedback for different conditions.
 *
 * Key features:
 * - Responsive design with column visibility control
 * - Visual state indicators for asset status
 * - Truncation handling for long text
 * - Special styling for scrapped assets
 * - Integration with user permissions
 * - Support for various data formats (dates, addresses)
 */
import { DetailedAsset } from '@/asset/api/types';
import { AssetFunctionalStatusIndicator } from '@/asset/components/functional-status-indicator';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { Link } from '@/components/link';
import { OverflowTypography } from '@/components/overflow-typography';
import { AssetTableColumn } from '@/constants/asset-table-column';
import {
  AdsClickOutlined,
  KeyboardTabOutlined,
  LocalShippingOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
} from '@/location/utilities/get-effective-third-party';
import { Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

/**
 * Props for configuring the AssetTableRow component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * The asset to display in the row.
   * Contains all information about the asset including its
   * serial number, location, owner, and activity history.
   *
   * Example:
   * ```ts
   * {
   *   key: 'asset-123',
   *   serial: 'SN123456',
   *   description: 'Main processing unit',
   *   functionalStatus: 'OPERATIONAL',
   *   product: { name: 'Processor X1' },
   *   currentLocation: { name: 'Warehouse A' }
   * }
   * ```
   */
  asset: DetailedAsset;

  /**
   * The columns to display in the row.
   * Determines which asset properties are visible.
   * Some columns are hidden on mobile devices for better UX.
   *
   * Available columns:
   * - 'serial': Asset serial number and status
   * - 'product': Product name and details
   * - 'project': Associated project information
   * - 'location': Current location and address
   * - 'owner': Current owner information
   * - 'activity': Recent events and timestamps
   *
   * Example: ['serial', 'location', 'owner']
   */
  columns: AssetTableColumn[];

  /**
   * Layout for recent activity entries.
   */
  activityLayout?: 'inline' | 'stacked';
}

/**
 * A table row component that displays detailed asset information.
 * Renders asset details in a responsive format, adapting the display
 * based on screen size and the asset's status. Scrapped assets are
 * displayed with disabled text colors.
 *
 * Features:
 * - Clickable serial number linking to asset details
 * - Status indicator showing functional state
 * - Responsive column visibility based on screen size
 * - Special handling for scrapped assets with muted colors
 * - Recent activity display with formatted timestamps
 * - Location display with detailed address information
 * - Truncation handling for long descriptions
 * - Fallback displays for missing data
 *
 * Visual States:
 * - Normal: Standard text colors for operational assets
 * - Scrapped: Muted colors indicating decommissioned state
 * - Empty: Placeholder text for missing information
 * - Mobile: Simplified layout with fewer visible columns
 *
 * Usage example:
 * ```tsx
 * <AssetTableRow
 *   asset={{
 *     key: 'asset-123',
 *     serial: 'SN123456',
 *     description: 'Main unit',
 *     functionalStatus: 'OPERATIONAL'
 *   }}
 *   columns={['serial', 'location', 'owner']}
 * />
 * ```
 *
 * @param props - Configuration options for the row
 */
export function AssetTableRow(props: Props) {
  const { user } = useCurrentUser();

  // Determine owner (customer or organization)
  const owner = props.asset.ownership?.customer ?? user.organization;

  // Determine which columns to display
  const showSerial = props.columns.includes('serial');
  const showProject = props.columns.includes('project');
  const showProduct = props.columns.includes('product');
  const showLocation = props.columns.includes('location');
  const showOwner = props.columns.includes('owner');
  const showZone = props.columns.includes('zone');
  const showActivity = props.columns.includes('activity');

  // Adjust text colors based on asset status
  const primaryTextColor =
    props.asset.functionalStatus === 'SCRAPPED'
      ? 'text.disabled'
      : 'text.primary';

  const secondaryTextColor =
    props.asset.functionalStatus === 'SCRAPPED'
      ? 'text.disabled'
      : 'text.secondary';

  return (
    <TableRow>
      {showSerial && (
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Link
              to="/assets/$assetKey"
              color="primary.main"
              variant="subtitle2"
              params={{ assetKey: props.asset.key }}
            >
              {props.asset.serial}
            </Link>
            <AssetFunctionalStatusIndicator
              status={props.asset.functionalStatus}
            />
          </Stack>
          <OverflowTypography
            color={secondaryTextColor}
            variant="body2"
            maxWidth="10rem"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            overflow="hidden"
          >
            {props.asset.description}
          </OverflowTypography>
        </TableCell>
      )}
      {showProduct && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          <Typography variant="body2" color={primaryTextColor}>
            {props.asset.item.sku}
            <Typography
              component="span"
              variant="body2"
              color={secondaryTextColor}
            >
              {' '}
              ({props.asset.item.name})
            </Typography>
          </Typography>
        </TableCell>
      )}
      {showProject && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          {props.asset.project === null ? (
            <Typography variant="body2" color={secondaryTextColor}>
              None
            </Typography>
          ) : (
            <Typography variant="body2" color={primaryTextColor}>
              {props.asset.project.name}
            </Typography>
          )}
        </TableCell>
      )}
      {showLocation &&
        (props.asset.currentLocation ? (
          <TableCell>
            <Typography variant="body2" color={primaryTextColor}>
              {getEffectiveLocationName(props.asset.currentLocation)}
            </Typography>
            <Typography variant="body2" color={secondaryTextColor}>
              {getEffectiveAddress(props.asset.currentLocation)}
            </Typography>
          </TableCell>
        ) : (
          <TableCell>
            <Typography variant="body2" color={primaryTextColor}>
              In transit
            </Typography>
          </TableCell>
        ))}
      {showZone && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          {props.asset.currentLocation ? (
            props.asset.currentLocation.type === 'ZONE' ? (
              <Typography variant="body2" color={primaryTextColor}>
                {props.asset.currentLocation.name}
              </Typography>
            ) : (
              <Typography variant="body2" color={primaryTextColor}>
                Default
              </Typography>
            )
          ) : (
            <Typography variant="body2" color={secondaryTextColor}>
              —
            </Typography>
          )}
        </TableCell>
      )}
      {showOwner && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          <Typography variant="body2" color={primaryTextColor}>
            {owner.name}
          </Typography>
        </TableCell>
      )}
      {showActivity && (
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          {props.asset.activity.length === 0 ? (
            <Typography variant="body2" color={secondaryTextColor}>
              None
            </Typography>
          ) : (
            <Stack component="ul" spacing={1} margin={0} padding={0}>
              {[...props.asset.activity]
                .sort((a, b) => {
                  // Defensive sort: ensure events are ordered by occurredAt DESC (most recent first)
                  const aTime = a.occurredAt?.toMillis() ?? 0;
                  const bTime = b.occurredAt?.toMillis() ?? 0;
                  return bTime - aTime;
                })
                .slice(0, 2)
                .map((event) => {
                  const eventTypeName = event.type.name.toLowerCase();
                  const isShipEvent = eventTypeName.includes('ship');
                  const isDeliverEvent = eventTypeName.includes('deliver');

                  const dateDisplay =
                    event.occurredAt?.toLocaleString({
                      dateStyle: 'medium',
                    }) ?? 'Not reported';

                  const fullDateTimeDisplay = event.occurredAt
                    ? event.occurredAt
                        .setZone(user.organization.timezone)
                        .toLocaleString({
                          dateStyle: 'long',
                          timeStyle: 'long',
                        })
                    : null;

                  return (
                    <Stack
                      key={event.id}
                      component="li"
                      direction={
                        props.activityLayout === 'stacked' ? 'column' : 'row'
                      }
                      alignItems={
                        props.activityLayout === 'stacked'
                          ? 'flex-start'
                          : 'center'
                      }
                      spacing={0.5}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {isShipEvent ? (
                          <LocalShippingOutlined
                            sx={{ fontSize: '1rem', color: secondaryTextColor }}
                          />
                        ) : isDeliverEvent ? (
                          <KeyboardTabOutlined
                            sx={{ fontSize: '1rem', color: secondaryTextColor }}
                          />
                        ) : (
                          <AdsClickOutlined
                            sx={{ fontSize: '1rem', color: secondaryTextColor }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={primaryTextColor}
                          fontWeight="medium"
                        >
                          {event.type.name}
                        </Typography>
                      </Stack>
                      {fullDateTimeDisplay ? (
                        <Tooltip title={fullDateTimeDisplay} arrow>
                          <Typography
                            variant="body2"
                            color={secondaryTextColor}
                            sx={{
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              cursor: 'default',
                              marginLeft:
                                props.activityLayout === 'stacked' ? 2.5 : 0,
                              whiteSpace:
                                props.activityLayout === 'stacked'
                                  ? 'nowrap'
                                  : 'normal',
                            }}
                          >
                            {dateDisplay}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          color={secondaryTextColor}
                          sx={{
                            marginLeft:
                              props.activityLayout === 'stacked' ? 2.5 : 0,
                            whiteSpace:
                              props.activityLayout === 'stacked'
                                ? 'nowrap'
                                : 'normal',
                          }}
                        >
                          {dateDisplay}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
            </Stack>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
